<template>
  <esg-base-chart height="400px">
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <div v-if="!loading && series.length === 0"
         class="text-center">데이터가 없습니다
    </div>
    <apex-chart type="line"
                ref="chart"
                height="380px"
                v-else
                v-show="!loading"
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>
<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  name: 'CarbonNeutralCostByYear',
  components: { EsgBaseChart },
  props: {
    type: {
      type: String,
      default: ''
    },
    corporation: {
      type: Object,
      default() {
        return {}
      }
    },
    businessLocation: {
      type: Object,
      default() {
        return {}
      }
    },
    firstYear: {
      type: Number,
      default: 2018,
    },
    lastYear: {
      type: Number,
      default: 2050,
    },
  },
  created() {
    this.fetchData();
  },
  watch: {
    type: {
      handler() {
        this.fetchData();
      }
    },
    corporation: {
      deep: true,
      handler() {
        this.fetchData();
      }
    },
    businessLocation: {
      deep: true,
      handler() {
        this.fetchData();
      }
    },
    firstYear: {
      immediate: true,
      handler() {
        const years = Array.from({ length: this.lastYear - this.firstYear + 1 }, (_, index) => this.firstYear + index);
        this.setData(this.list, years);
      }
    },
    lastYear: {
      immediate: true,
      handler() {
        const years = Array.from({ length: this.lastYear - this.firstYear + 1 }, (_, index) => this.firstYear + index);
        this.setData(this.list, years);
      }
    }
  },
  data() {
    return {
      loading: false,
      list: [],
      years: [],
      series: [],
      chartOptions: {
        chart: {
          zoom: { enabled: false },
          toolbar: { show: false }
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [],
          offsetY: -5,
          formatter: val => this.formatNumber(val / 10e8, 0),
          style: {
            colors: ['#000']
          }
        },
        stroke: {
          width: 3,
          curve: 'straight',
        },
        labels: [],
        grid: {
          show: false,
          padding: {
            right: 20
          }
        },
        yaxis: {
          show: true,
          title: {
            text: '비용 (억원)',
            style: {
              fontSize: '12px',
              fontWeight: 500
            }
          },
          labels: {
            formatter: val => this.compactFormatNumber(val / 10e8)
          }
        },
        xaxis: {
          type: 'datetime',
          crosshairs: { show: false },
          tooltip: { enabled: false },
        },
        tooltip: {
          x: {
            formatter: val => new Date(val).getFullYear()
          },
          y: {
            formatter: val => this.formatNumber(val / 10e8) + '억 원'
          }
        },
        markers: {
          size: 4,
          strokeWidth: 3,
          shape: "circle",
          hover: {
            sizeOffset: 2
          }
        },
      },
    }
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        let args = null
        if (!this.type) {
          args = { groupKeys: ['cost_type', 'year'] };
        } else if (['C', 'O'].includes(this.type)) {
          args = { groupKeys: ['cost_type', 'abatement_method_value', 'abatement_method_name', 'year'] };
        }
        this.corporation.name && args && (args.corporations = [this.corporation.code])
        this.businessLocation.name && args && (args.businessLocations = [this.businessLocation.business_location_code])

        let data = new FormData()
        data.append('path', 'esg/cost/main');
        data.append('json', JSON.stringify([args]));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }

        let list = JSON.parse(response.data.data)[0].data
        this.list = list;

        // Set years starting from 2018 to 2050
        const years = Array.from({ length: this.lastYear - this.firstYear + 1 }, (_, index) => this.firstYear + index);
        this.years = years;

        this.setData(list, years);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    setData(list, years) {
      if (!this.type) {
        this.setSeries(list, years);
      } else if (['C', 'O'].includes(this.type)) {
        this.setDetailedSeries(list, years);
      }
      this.setLabels(list, years);
      this.refreshChart();
    },
    setSeries(list, years) {
      // Raw data for each cost type, CAPEX/OPEX
      let capexRawData = list.filter(row => row.cost_type === 'C');
      let opexRawData = list.filter(row => row.cost_type === 'O');

      // Chart data
      const capex = [];
      const opex = [];
      const total = [];

      for (let year of years) {
        // For each year, check if the data exists
        let capexRow = capexRawData.find(row => row.year === year);
        let opexRow = opexRawData.find(row => row.year === year);

        // If it does not exist, set it to 0
        let capexValue = capexRow ? capexRow.cost_total : 0;
        let opexValue = opexRow ? opexRow.cost_total : 0;

        // Push the cost_total to the corresponding array
        capex.push(capexValue);
        opex.push(opexValue);
        total.push(capexValue + opexValue);
      }

      // Calculate cumulative sum
      const cumsum = [];
      let sum = 0;
      for (let value of total) {
        sum += value;
        cumsum.push(sum);
      }

      this.series = [
        // 23-10-12 윤지원: 문경태 팀장님 요청에 의해 CAPEX/OPEX 제거
        // { name: 'CAPEX', data: capex },
        // { name: 'OPEX', data: opex },
        { name: '합계', data: total },
        { name: '누계', data: cumsum }
      ]

      // 마지막 누계에 대해서만 데이터 레이블 적용
      this.chartOptions.dataLabels.enabledOnSeries = [this.series.length - 1]
    },
    setDetailedSeries(list, years) {
      // Filter data
      const filtered = list.filter(row => row.cost_type === this.type)

      // Extract abatement method names
      const abatementMethods = Array.from(new Set(filtered.map(row => row.abatement_method_name)));
      abatementMethods.sort((a, b) => a.localeCompare(b))

      // Construct chart series
      let series = {}
      for (let year of years) {
        for (let method of abatementMethods) {
          // Find a row with the given year and abatement method name
          let row = filtered.find(row => row.year === year && row.abatement_method_name === method);

          // If it exists, then retrieve the value. If not, set it to zero
          let value = row ? row.cost_total : 0;

          if (series[method]) {
            // If a field with the method name exists, add the value to its data
            series[method].data.push(value);
          } else {
            // Else, create a new field
            series[method] = {
              name: method,
              data: [value]
            }
          }
        }
      }

      // Calculate totals
      let totals = [];
      for (let key in series) {
        let data = series[key].data;
        if (data && Array.isArray(data)) {
          if (totals.length === 0) {
            totals = [...data];
          } else {
            for (let i = 0; i < data.length; i++) totals[i] += data[i];
          }
        }
      }

      // Calculate cumulative sums
      let cumsums = [];
      let sum = 0;
      for (let i = 0; i < totals.length; i++) {
        sum += totals[i];
        cumsums.push(sum);
      }

      // Add the results to the series
      Object.assign(series, {
        totals: { name: '합계', data: totals },
        cumsums: { name: '누계', data: cumsums }
      })

      this.series = Object.values(series);

      // 마지막 누계에 대해서만 데이터 레이블 적용
      this.chartOptions.dataLabels.enabledOnSeries = [this.series.length - 1]
    },
    setLabels(list, years) {
      this.chartOptions.labels = years.map(year => `01/01/${year}`)
    }
  }
}
</script>
